import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { CameraControls } from "@react-three/drei";
import {
  createDollyBlock,
  createPanBlock,
  createTruckBlock,
  createTiltBlock,
  createPedestalBlock,
  createRollBlock,
  createZoomBlock,
  createDollyZoomBlock,
  createArcBlock,
  createCompositeBlock,
  createMoveToBlock,
  createBezierCurveBlock,
  MotionBlock,
  MotionBlockOptions
} from "./motionBlocks";

// Block configuration interface extends MotionBlockOptions
export interface BlockConfig extends MotionBlockOptions {
  id: string;
  enabled?: boolean; // If false, block is skipped during playback (default: true)
}

interface MotionSequencerProps {
  isPlaying: boolean;
  sequenceIds: string[] | BlockConfig[]; // Support both string array and config array
  modelRadius?: number;
  onComplete?: () => void;
}

export const MotionSequencer = ({ 
  isPlaying, 
  sequenceIds, 
  modelRadius = 2, 
  onComplete 
}: MotionSequencerProps) => {
  const controls = useThree((state) => state.controls as CameraControls | null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);
  // [FIX] Use Ref to ensure original settings persist across render cycles
  const originalSmoothTimeRef = useRef<number>(0.05);

  // Motion block factory mapping - all factories now accept unified MotionBlockOptions
  const blockMap: Record<string, (opts: MotionBlockOptions) => MotionBlock> = {
    dolly: createDollyBlock,
    pan: createPanBlock,
    truck: createTruckBlock,
    tilt: createTiltBlock,
    pedestal: createPedestalBlock,
    roll: createRollBlock,
    zoom: createZoomBlock,
    dollyZoom: createDollyZoomBlock,
    arc: createArcBlock,
    composite: createCompositeBlock,
    moveTo: createMoveToBlock,
    bezierCurve: createBezierCurveBlock,
  };

  useEffect(() => {
    if (!controls) return;

    if (isPlaying) {
      // 1. Clear old animation and reset
      timelineRef.current?.kill();
      
      // 2. [FIX] Safely save original smoothTime using ref
      originalSmoothTimeRef.current = controls.smoothTime;
      controls.smoothTime = 0.05; // Temporarily reduce smoothTime to work with GSAP (in seconds)
      
      const mainTl = gsap.timeline({ 
        onComplete: () => {
          // Restore smoothTime when animation completes
          controls.smoothTime = originalSmoothTimeRef.current;
          onComplete?.();
        }
      });

      // 3. Assemble sequence - blocks run sequentially without overlap
      // Each block captures state when it actually starts (via onStart), ensuring continuity
      sequenceIds.forEach((item) => {
        // Support both string (backward compatibility) and BlockConfig
        const config: BlockConfig = typeof item === 'string' 
          ? { id: item } 
          : item;
        
        // Skip disabled blocks (enabled defaults to true for backward compatibility)
        if (config.enabled === false) {
          return;
        }
        
        // Extract block type from id (e.g., "moveTo-1234567890" -> "moveTo")
        const blockType = config.id.split('-')[0];
        const blockFactory = blockMap[blockType];
        if (blockFactory) {
          // No need to manually extract parameters - just pass the config object
          // This makes it extensible: adding ease, delay, startPos, etc. doesn't require code changes here
          const block = blockFactory(config);
          const blockTimeline = block.execute({ controls, radius: modelRadius });
          // Add blocks sequentially without overlap
          mainTl.add(blockTimeline);
        }
      });

      timelineRef.current = mainTl;
    } else {
      // Pause logic
      timelineRef.current?.pause();
    }

    return () => {
      timelineRef.current?.kill();
      // Restore smoothTime on cleanup
      if (controls) {
        controls.smoothTime = originalSmoothTimeRef.current;
      }
    };
  }, [isPlaying, sequenceIds, controls, modelRadius, onComplete]);

  return null; // No rendering needed
};
